import { Button } from '@/components/ui/button';
import { Check, Copy } from 'lucide-react';
import { useRef, useState } from 'react';

/**
 * A read-only monospace block with a copy button (§20.2, issue #84).
 *
 * The three things the one-time panel hands over - the token itself and the
 * two command blocks - are the same object: text nobody types, that has to
 * leave this page in one gesture, and that must stay selectable when the
 * gesture fails.
 *
 * ## `readOnly` and not `disabled`
 *
 * The rule `liens/link-url-field.tsx` and the criterion editor both state: a
 * disabled control leaves the tab order and its text stops being selectable,
 * which would make the one field of this screen whose entire purpose is to be
 * copied the only one that cannot be. It is read-only because the value is not
 * an operator's to edit, not because it is inert.
 *
 * ## The copy has a fallback, and the fallback is the field itself
 *
 * `navigator.clipboard` is absent on an insecure origin and can be refused by
 * permission, and this screen is opened over plain HTTP on a dev stack every
 * day. So a failed write SELECTS the text and says so: the operator finishes
 * with Ctrl+C rather than facing a button that did nothing. Here the stake is
 * higher than it was for a link - the token is shown ONCE, so a copy that
 * looked like a success and was not costs a re-mint.
 *
 * ## A `<textarea>` and not an `<input>`
 *
 * A command block is two lines, and the token is one. One element for the
 * three rather than two spellings of « the same block »: `select()` works the
 * same on both, and a single-line textarea with `resize-none` is
 * indistinguishable from an input in the maquette's terms.
 */

export interface TokenCopyFieldProps {
  readonly value: string;
  /** The accessible name of the field - it carries no visible label of its own. */
  readonly label: string;
  /** What the button says: « Copier le jeton », « Copier les commandes ». */
  readonly buttonLabel: string;
  /** Two for a command block, one for the token. */
  readonly rows?: number;
  readonly testId: string;
}

export function TokenCopyField({
  value,
  label,
  buttonLabel,
  rows = 1,
  testId,
}: TokenCopyFieldProps) {
  const field = useRef<HTMLTextAreaElement>(null);
  const [state, setState] = useState<'idle' | 'copied' | 'select'>('idle');

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setState('copied');
    } catch {
      // No clipboard, or refused: hand the operator the selection instead.
      field.current?.select();
      setState('select');
    }
  }

  return (
    <div className="space-y-1">
      <div className="flex items-start gap-2">
        <textarea
          ref={field}
          readOnly
          rows={rows}
          aria-label={label}
          data-testid={testId}
          value={value}
          onFocus={(event) => event.currentTarget.select()}
          className="min-w-0 flex-1 resize-none rounded-md border-2 border-gray-200 bg-gray-50 px-3 py-2 font-mono text-gray-700 text-xs leading-5 read-only:text-gray-700"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid={`${testId}-copier`}
          onClick={() => void copy()}
        >
          {state === 'copied' ? (
            <Check size={16} strokeWidth={2} aria-hidden="true" />
          ) : (
            <Copy size={16} strokeWidth={2} aria-hidden="true" />
          )}
          {buttonLabel}
        </Button>
      </div>
      {/* Mounted from the first render so the message is announced when it
          arrives, and worded for the outcome that actually happened. */}
      <p aria-live="polite" className="text-gray-600 text-xs">
        {state === 'copied' ? 'Copié.' : null}
        {state === 'select'
          ? 'Le presse-papiers est indisponible : le texte est sélectionné.'
          : null}
      </p>
    </div>
  );
}
